import { BuildingComponents, RowElementType } from "./BuildingComponents";
import { FC, useState } from "react";
import { ComponentsTableOptions } from "./ComponentsTableOptions";
import { Row } from "taltech-styleguide";
import { useWizard } from "../../wizard/context";
import { ProgressWizardStepForm } from "../../wizard/ProgressWizardStepForm";
import { partitionBy } from "../../../../../components-fabric/formats/predicates";
import { isEmpty } from "ramda";
import buildingComponentSchema from "./building-components.schema";
import { useNavigate } from "react-router-dom";
import { calculateLowestRoomLength } from "../../wizard/helpers";

/**
 * Functional Component: BuildingComponentsAndWindowComponents
 *
 * This component renders a form for configuring building and window components.
 * It partitions dropdown options based on categories and handles the selection of components.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.title - The title of the form.
 * @param {Object} props.element - The element object containing element types.
 * @param {Object} props.dropdownOptions - The dropdown options for each component type.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the building and window components form.
 */
export const BuildingComponentsAndWindowComponents = ({ ...props }) => {
  const wizard = useWizard();
  const { title, element } = props;
  const [selectedType, setSelectedType] = useState("");
  const rows = [
    "wallInterior",
    "wallExterior",
    "floor",
    "floorGround",
    "roof",
    "windowType",
  ];
  const { modular, moduleSize } = wizard.state.steps[0].data;
  const { rooms: firstRowRooms } = wizard.state.steps[1].data.rowFirst;
  const { rooms: secondRowRooms } = wizard.state.steps[1].data.rowSecond;

  const lowestRoomLength = calculateLowestRoomLength(
    firstRowRooms,
    secondRowRooms,
    modular,
    moduleSize
  );

  props.dropdownOptions = partitionBy([
    {
      filter: (x: any) => x.category === "WALLS" && x.side === "INTERIOR",
      prop: "wallInterior",
    },
    {
      filter: (x: any) => x.category === "WALLS" && x.side === "EXTERIOR",
      prop: "wallExterior",
    },
    { filter: (x: any) => x.category === "FLOORS", prop: "floor" },
    { filter: (x: any) => x.category === "FLOORS", prop: "floorGround" },
    { filter: (x: any) => x.category === "ROOFS", prop: "roof" },
    { filter: (x: any) => x.category === "WINDOWS", prop: "windowType" },
  ])(wizard.state.elementTypesOptions);
  props.dropdownOptions.floorGround = props.dropdownOptions.floor;

  if (props.dropdownOptions.windowType !== undefined) {
    props.dropdownOptions.windowType = props.dropdownOptions.windowType
      .map((x: any) => {
        if (x.width.position === "ABSOLUTE") {
          if (x.style === "WALL_TO_WALL") {
            if (x.width.length <= lowestRoomLength) {
              return x;
            }
          } else {
            if (x.width.length <= moduleSize) {
              return x;
            }
          }

          return null;
        } else return x;
      })
      .filter((x: any) => x !== null);
    props.dropdownOptions.windowType =
      props.dropdownOptions.windowType.length > 0
        ? props.dropdownOptions.windowType
        : undefined;
  }

  if (isEmpty(wizard.state.step.data)) {
    rows.forEach((row: string) => {
      if (props.dropdownOptions[row] !== undefined) {
        element.elementTypes[row].options.elements =
          props.dropdownOptions[row][0].id;
      }
    });
  }

  return (
    <ProgressWizardStepForm
      element={element.elementTypes}
      title={title}
      schema={buildingComponentSchema}
    >
      <Row className="flex-column gap-3">
        {rows.map((row: string) => {
          if (!props.dropdownOptions[row] && row === "wallInterior") {
            return <MissingTypes key={row} row={row} />;
          } else if (!props.dropdownOptions[row] && row === "floor") {
            return <MissingTypes key={row} row={row} />;
          } else if (!props.dropdownOptions[row] && row === "roof") {
            return <MissingTypes key={row} row={row} />;
          } else if (!props.dropdownOptions[row] && row === "windowType") {
            return <MissingTypes key={row} row={row} />;
          }
        })}
        <BuildingComponents
          rows={rows}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          dropdownOptions={props.dropdownOptions}
        />
        <ComponentsTableOptions
          selectedType={selectedType}
          dropdownOptions={props.dropdownOptions}
        />
      </Row>
    </ProgressWizardStepForm>
  );
};

/**
 * Functional Component: MissingTypes
 *
 * This component renders a message indicating that no types are available for the given row.
 * It provides a link to navigate to the page for creating the missing types.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {RowElementType} props.row - The row element type.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the missing types message.
 */
const MissingTypes: FC<{ row: RowElementType }> = ({ row }) => {
  const nav = useNavigate();
  const missingType: { text: string; goTo: string } = mappedMissingType(row);
  return (
    <p className="mb-2">
      You do not have any {missingType.text} created. Create{" "}
      <a
        className="link"
        style={{ cursor: "pointer" }}
        onClick={() => nav(missingType.goTo)}
      >
        here
      </a>
      .
    </p>
  );
};

/**
 * Function: mappedMissingType
 *
 * This function maps a row element type to a corresponding missing type object containing text and navigation path.
 *
 * @param {RowElementType} row - The row element type.
 * @returns {Object} An object containing the text and navigation path for the missing type.
 */
const mappedMissingType = (row: RowElementType) => {
  switch (row) {
    case "wallInterior":
    case "wallExterior":
      return {
        text: "wall types",
        goTo: "/components-db/components/walls/types/new",
      };
    case "floor":
    case "floorGround":
      return {
        text: "floor types",
        goTo: "/components-db/components/floors/types/new",
      };
    case "roof":
      return {
        text: "roof types",
        goTo: "/components-db/components/roofs/types/new",
      };
    case "windowType":
      return {
        text: "window types",
        goTo: "/components-db/components/windows/types/new",
      };
    default:
      return { text: "types", goTo: "/components-db" };
  }
};
